import { Icon, tokens } from '@/design';
import type { LayoutWorkspaceView } from './layoutTypes';

export interface LayoutWorkspaceBarProps {
  readonly view: LayoutWorkspaceView;
  readonly onViewChange: (view: LayoutWorkspaceView) => void;
}

const VIEWS: { id: LayoutWorkspaceView; label: string; icon: 'map' | 'crop_landscape' | 'splitscreen' }[] = [
  { id: 'map', label: 'Map', icon: 'map' },
  { id: 'layout', label: 'Layout', icon: 'crop_landscape' },
  { id: 'split', label: 'Side by side', icon: 'splitscreen' },
];

export function LayoutWorkspaceBar({ view, onViewChange }: LayoutWorkspaceBarProps) {
  return (
    <div
      role="tablist"
      aria-label="Map and layout views"
      data-testid="layout-workspace-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        borderBottom: tokens.border.thin,
        background: tokens.color.surface[1],
        flex: '0 0 auto',
      }}
    >
      {VIEWS.map((item) => {
        const selected = view === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`layout-workspace-tab-${item.id}`}
            onClick={() => onViewChange(item.id)}
            style={{
              minHeight: tokens.density.touchMin,
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              padding: `0 ${tokens.space[3]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: selected ? tokens.color.accent.subtle : tokens.color.surface[0],
              color: tokens.color.ink.primary,
              cursor: 'pointer',
              fontSize: tokens.font.size.sm,
              fontFamily: tokens.font.family.ui,
              fontWeight: selected ? tokens.font.weight.semibold : tokens.font.weight.regular,
            }}
          >
            <Icon name={item.icon} size={16} decorative />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
